import { useMemo } from "react";
import { useObserverStore } from "../../stores/observerStore";
import type { StarSystem } from "../../types/celestial";

/** Side panel showing agents at the focused system */
export function AgentPanel(): React.JSX.Element | null {
	const focusedSystem = useObserverStore((s) => s.focusedSystem);
	const agents = useObserverStore((s) => s.agents);
	const activeTravels = useObserverStore((s) => s.activeTravels);
	const systems = useObserverStore((s) => s.systems);

	// Build planet→system lookup
	const systemLookup = useMemo(() => {
		const map = new Map<string, StarSystem>();
		for (const sys of systems) {
			map.set(sys.id, sys);
			for (const p of sys.planets) {
				map.set(p.id, sys);
			}
		}
		return map;
	}, [systems]);

	if (!focusedSystem) return null;

	// Find agents at this system
	const agentsHere = Array.from(agents.values()).filter((a) => {
		const sys = systemLookup.get(a.locationPlanetId);
		return sys?.id === focusedSystem.id;
	});

	// Find travels arriving at this system
	const arrivingHere = activeTravels.filter((t) => {
		const sys = systemLookup.get(t.toPlanetId);
		return sys?.id === focusedSystem.id;
	});

	if (agentsHere.length === 0 && arrivingHere.length === 0) return null;

	return (
		<div
			style={{
				position: "absolute",
				top: "60px",
				left: "12px",
				width: "240px",
				background: "rgba(10, 10, 15, 0.9)",
				backdropFilter: "blur(8px)",
				border: "1px solid rgba(26, 26, 46, 0.8)",
				borderRadius: "6px",
				padding: "0.6rem",
				zIndex: 20,
				fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
				fontSize: "0.7rem",
			}}
		>
			<div
				style={{
					color: "#7aa2f7",
					fontSize: "0.75rem",
					fontWeight: 600,
					marginBottom: "0.5rem",
				}}
			>
				{focusedSystem.name}
			</div>

			{agentsHere.length > 0 && (
				<>
					<div
						style={{
							color: "#565680",
							fontSize: "0.6rem",
							textTransform: "uppercase",
							letterSpacing: "0.1em",
							marginBottom: "0.3rem",
						}}
					>
						Docked ({agentsHere.length})
					</div>
					{agentsHere.map((agent) => (
						<div
							key={agent.id}
							style={{
								display: "flex",
								justifyContent: "space-between",
								padding: "0.2rem 0",
								borderBottom: "1px solid rgba(26, 26, 46, 0.5)",
							}}
						>
							<span style={{ color: "#c8c8d0" }}>{agent.name}</span>
							<span style={{ color: "#e0af68" }}>{Math.round(agent.credits)}cr</span>
						</div>
					))}
				</>
			)}

			{arrivingHere.length > 0 && (
				<>
					<div
						style={{
							color: "#565680",
							fontSize: "0.6rem",
							textTransform: "uppercase",
							letterSpacing: "0.1em",
							marginTop: "0.5rem",
							marginBottom: "0.3rem",
						}}
					>
						Arriving ({arrivingHere.length})
					</div>
					{arrivingHere.map((travel) => {
						const remaining = Math.max(0, Math.floor((travel.arrivesAt - Date.now()) / 1000));
						const min = Math.floor(remaining / 60);
						const sec = remaining % 60;
						return (
							<div
								key={`${travel.agentId}-${travel.departedAt}`}
								style={{
									display: "flex",
									justifyContent: "space-between",
									padding: "0.2rem 0",
									color: "#7aa2f7",
								}}
							>
								<span>{travel.agentName || travel.agentId.slice(0, 8)}</span>
								<span>
									{min}m {sec}s
								</span>
							</div>
						);
					})}
				</>
			)}
		</div>
	);
}
